import React, { useCallback, useEffect, useState } from "react";
import { Button, message } from "antd";
import { PageSizeType, TemplatesTypes, PageOrientation } from "../templates";
import { useIsMobile, useIsTablet } from "../../Hook";
import { useCart } from "../../Context";
import { useReturnInvoiceData } from "./useReturnInvoiceData";
import { useReturnInvoiceDocument } from "./useReturnInvoiceDocument";
import { useSystemConfig } from "../../Hook/useSystemConfig";
import { DRAWER_FORM_WIDTH, drawerTitle } from "../AppUi";
import { PrintConfigSelector } from "../PrintConfigSelector/PrintConfigSelector";
import { ResizableDrawer } from "../ResizableDrawer/ResizableDrawer";
import { InvoiceDrawerFooter } from "../InvoiceDrawer/InvoiceDrawerFooter";
import { useInvoiceSharing } from "../InvoiceDrawer/useInvoiceSharing";

interface ReturnInvoiceDrawerProps {
  open: boolean;
  returnId: string | null;
  returnNo?: string;
  creditOnly?: boolean;
  onClose: () => void;
}

export const ReturnInvoiceDrawer: React.FC<ReturnInvoiceDrawerProps> = ({
  open,
  returnId,
  returnNo,
  creditOnly = false,
  onClose,
}) => {
  const isMobile = useIsMobile();
  const isTablet = useIsTablet();
  const [pageSize, setPageSize] = useState<PageSizeType>("A4");
  const [orientation, setOrientation] = useState<PageOrientation>("portrait");
  const [template, setTemplate] = useState<TemplatesTypes>(
    TemplatesTypes.Classic,
  );
  const [creditNoteOpen, setCreditNoteOpen] = useState(false);
  const { billType, orientation: savedOrientation } = useCart();

  const { orderReturn, config } = useReturnInvoiceData({ open, returnId });
  const { getReturnHtml, printInvoice, downloadInvoice, getReturnPdfBase64 } =
    useReturnInvoiceDocument({
      orderReturn,
      config,
      pageSize,
      orientation,
      template,
      creditOnly,
    });

  const documentLabel = creditOnly ? "Customer Credit" : "Return Invoice";

  const [whatsappPhoneState, setWhatsappPhoneState] = useState<string>("");

  useEffect(() => {
    if (open && orderReturn) {
      const phoneVal = String(
        orderReturn.customer_phone ||
          (orderReturn.customer as any)?.phone ||
          config?.whatsapp ||
          "",
      ).trim();
      setWhatsappPhoneState(phoneVal);
    }
  }, [open, orderReturn, config]);

  const whatsappPhone = whatsappPhoneState.trim()
    ? whatsappPhoneState
    : String(
        orderReturn?.customer_phone ||
          (orderReturn?.customer as any)?.phone ||
          config?.whatsapp ||
          "",
      );

  const { isWhatsAppEnabled, isSmsGatewayEnable } = useSystemConfig();
  const isWhatsAppConnected = true;

  const {
    handleSendWhatsAppPdf,
    handleSendWhatsAppLink: sendWhatsAppLink,
    handleSendSMSLink: sendSMSLink,
    isSendWhatsAppPending,
    isSendWhatsAppLinkPending,
    isSendSmsLinkPending,
  } = useInvoiceSharing();

  const handleSendWhatsApp = () =>
    handleSendWhatsAppPdf({
      phone: whatsappPhone,
      documentId: orderReturn?.id || "",
      documentNo: orderReturn?.return_no,
      documentLabel,
      getPdfBase64: getReturnPdfBase64,
    });

  const handleSendWhatsAppLink = () =>
    sendWhatsAppLink({
      phone: whatsappPhone,
      documentNo: orderReturn?.return_no,
      documentLabel,
      partyType: "Customer",
      linkPath: `/returns/${orderReturn?.id || ""}`,
    });

  const handleSendSMSLink = () =>
    sendSMSLink({
      phone: whatsappPhone,
      documentNo: orderReturn?.return_no,
      documentLabel,
      partyType: "Customer",
      linkPath: `/returns/${orderReturn?.id || ""}`,
    });

  useEffect(() => {
    if (open) {
      setPageSize(billType);
      setOrientation(savedOrientation);
    }
  }, [open, billType, savedOrientation]);

  if (!orderReturn) return null;

  return (
    <>
      <ResizableDrawer
        open={open}
        onClose={onClose}
        title={drawerTitle(documentLabel + " Preview")}
        width={isMobile || isTablet ? "100%" : DRAWER_FORM_WIDTH}
        className="return-invoice-drawer"
        destroyOnClose
        footer={
          <InvoiceDrawerFooter
            onPrint={printInvoice}
            onDownload={downloadInvoice}
            extraActions={
              !creditOnly &&
              orderReturn.refund_type_code === "store_credit" &&
              Number(orderReturn.exchange_value || 0) > 0 && (
                <Button
                  size="middle"
                  onClick={() => setCreditNoteOpen(true)}
                  className="flex-grow min-w-[100px] text-xs font-semibold h-9 rounded-md"
                >
                  Customer Credit
                </Button>
              )
            }
            whatsappPhone={whatsappPhone}
            onWhatsappPhoneChange={setWhatsappPhoneState}
            onSendWhatsApp={handleSendWhatsApp}
            onSendWhatsAppLink={handleSendWhatsAppLink}
            onSendSMSLink={handleSendSMSLink}
            isSendWhatsAppPending={isSendWhatsAppPending}
            isSendWhatsAppLinkPending={isSendWhatsAppLinkPending}
            isSendSmsLinkPending={isSendSmsLinkPending}
            isWhatsAppEnabled={isWhatsAppEnabled}
            isWhatsAppConnected={isWhatsAppConnected}
            isSmsGatewayEnable={isSmsGatewayEnable}
            footerNote="Page size cycles in the select below"
          />
        }
      >
        <>
          <PrintConfigSelector
            template={template}
            pageSize={pageSize}
            orientation={orientation}
            onChangeTemplate={setTemplate}
            onChangePageSize={setPageSize}
            onChangeOrientation={setOrientation}
          />
          <div
            dangerouslySetInnerHTML={{ __html: getReturnHtml(false) }}
            style={{ width: "100%" }}
          />
        </>
      </ResizableDrawer>
      {creditNoteOpen && returnId && (
        <ReturnInvoiceDrawer
          open={creditNoteOpen}
          returnId={returnId}
          returnNo={returnNo}
          creditOnly
          onClose={() => setCreditNoteOpen(false)}
        />
      )}
    </>
  );
};
